import { useState, type CSSProperties } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Header } from "@/components/Header";
import { BeforeAfterDemo } from "@/components/BeforeAfterDemo";
import { useT, useDocumentTitleKey, useLocale } from "@/i18n/locale-context";
import { usePremium } from "@/hooks/usePremium";
import { PROTOCOLS } from "@/lib/protocols/definitions";

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: "Glow Up Coach — Coach photo IA hebdomadaire" },
      { name: "description", content: "Un selfie par semaine. Notre IA analyse votre peau, posture et vitalité. Au moins 3 conseils concrets. Hébergé en UE." },
      { property: "og:title", content: "Glow Up Coach — Coach photo IA" },
      { property: "og:description", content: "Analyse hebdomadaire de votre évolution faciale par IA. Privé. Européen." },
    ],
  }),
  component: Landing,
});

function Landing() {
  useDocumentTitleKey("index.head.title");
  const t = useT();
  const { isPremium } = usePremium();
  return (
    <div className="min-h-screen bg-background font-sans text-foreground relative overflow-hidden" style={{ fontFamily: "var(--font-sans)" }}>
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link href="https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&family=Space+Grotesk:wght@500;600;700&display=swap" rel="stylesheet" />
      {/* Slogans subliminaux : visibles mais discrets, dispersés à gauche/droite */}
      <SubliminalSlogans />
      <Header />

      <section className="pt-24 pb-16 px-6 relative" style={{ background: "var(--gradient-hero)" }}>
        <div className="max-w-5xl mx-auto text-center">
          <span className="inline-block px-4 py-1.5 rounded-full bg-accent/10 text-accent text-xs font-bold tracking-widest uppercase mb-6">
            {t("index.hero.badge")}
          </span>
          <h1 className="font-display text-5xl md:text-7xl font-bold mb-8 leading-[1.1] tracking-tight" style={{ fontFamily: "var(--font-display)" }}>
            {t("index.hero.title.start")} <span className="text-accent">{t("index.hero.title.accent")}</span>
          </h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto mb-10 leading-relaxed">
            {t("index.hero.subtitle")}
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3 mb-12">
            <Link to="/app" className="bg-foreground text-background px-8 py-3.5 rounded-full text-sm font-semibold hover:opacity-90 transition-opacity">
              {t("index.hero.cta.analyze")}
            </Link>
            <Link to="/" hash="demo" className="border border-foreground/15 px-8 py-3.5 rounded-full text-sm font-semibold hover:bg-foreground/5 transition-colors">
              {t("index.hero.cta.demo")}
            </Link>
          </div>

          <div id="privacy" className="flex items-center justify-center gap-3 mb-4">
            <div className="flex -space-x-2">
              <div className="size-10 rounded-full bg-success border-2 border-background grid place-items-center text-primary-foreground text-[10px] font-bold">GDPR</div>
              <div className="size-10 rounded-full bg-foreground border-2 border-background grid place-items-center text-background text-[10px] font-bold">EU</div>
            </div>
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider text-left">
              {t("index.hero.privacy.eu_line1")}<br />{t("index.hero.privacy.eu_line2")}
            </p>
          </div>
        </div>
      </section>

      {/* Manifeste — slogan principal mis en évidence */}
      <section aria-label="manifeste" className="px-6 -mt-4 mb-24 relative z-10">
        <div className="max-w-5xl mx-auto rounded-3xl border border-foreground/10 bg-foreground text-background p-10 md:p-14 text-center relative overflow-hidden" style={{ boxShadow: "var(--shadow-elegant)" }}>
          <div className="absolute inset-0 opacity-10 pointer-events-none" style={{ background: "radial-gradient(circle at 30% 20%, var(--accent), transparent 60%)" }} />
          <span className="relative inline-block text-[10px] font-bold uppercase tracking-[0.3em] text-accent mb-4">
            {t("slogan.highlight.kicker")}
          </span>
          <h2 className="relative font-display font-bold text-3xl md:text-5xl leading-[1.1] tracking-tight mb-5" style={{ fontFamily: "var(--font-display)" }}>
            {t("slogan.highlight.title")}
          </h2>
          <p className="relative text-base md:text-lg text-background/70 max-w-2xl mx-auto leading-relaxed">
            {t("slogan.highlight.body")}
          </p>
          <div className="relative mt-8 flex justify-center">
            <Link to="/quick" className="bg-accent text-accent-foreground px-8 py-3.5 rounded-full text-sm font-bold hover:opacity-90 transition-opacity">
              {t("index.hero.cta.quick")}
            </Link>
          </div>
        </div>
      </section>

      <section id="demo" className="max-w-7xl mx-auto px-6 mb-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          <div className="lg:col-span-8 bg-card rounded-3xl p-8 border border-foreground/5" style={{ boxShadow: "var(--shadow-elegant)" }}>
            <div className="flex items-center justify-between mb-8">
              <div>
                <h3 className="font-display font-bold text-2xl" style={{ fontFamily: "var(--font-display)" }}>{t("index.demo.week_title")}</h3>
                <p className="text-muted-foreground text-sm">{t("index.demo.week_goal")}</p>
              </div>
              <div className="flex gap-2">
                <span className="px-3 py-1 rounded-md bg-muted text-[10px] font-bold">S01</span>
                <span className="px-3 py-1 rounded-md bg-accent text-accent-foreground text-[10px] font-bold">S04</span>
              </div>
            </div>

            <div className="mb-8">
              <BeforeAfterDemo />
            </div>

            <div className="grid grid-cols-3 gap-6">
              {[
                { label: t("index.demo.metric.skin"), pct: 72, delta: "+12%", color: "bg-success", deltaColor: "text-success" },
                { label: t("index.demo.metric.posture"), pct: 64, delta: "+8%", color: "bg-success", deltaColor: "text-success" },
                { label: t("index.demo.metric.eye"), pct: 45, delta: t("index.demo.metric.stable"), color: "bg-muted-foreground/40", deltaColor: "text-muted-foreground" },
              ].map((m) => (
                <div key={m.label} className="space-y-2">
                  <div className="flex justify-between text-[11px] font-bold uppercase tracking-wider">
                    <span>{m.label}</span>
                    <span className={m.deltaColor}>{m.delta}</span>
                  </div>
                  <div className="h-1.5 w-full bg-muted rounded-full overflow-hidden">
                    <div className={`h-full ${m.color}`} style={{ width: `${m.pct}%` }} />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-4 space-y-6">
            <div className="bg-primary text-primary-foreground rounded-3xl p-8">
              <h4 className="font-display font-bold text-xl mb-6 flex items-center gap-2" style={{ fontFamily: "var(--font-display)" }}>
                <div className="size-2 bg-accent rounded-full animate-pulse" />
                {t("index.demo.plan.title")}
              </h4>
              <ul className="space-y-6">
                {[
                  { n: "01", title: t("index.demo.plan.t1.title"), text: t("index.demo.plan.t1.text") },
                  { n: "02", title: t("index.demo.plan.t2.title"), text: t("index.demo.plan.t2.text") },
                  { n: "03", title: t("index.demo.plan.t3.title"), text: t("index.demo.plan.t3.text") },
                ].map((p) => (
                  <li key={p.n} className="flex gap-4">
                    <span className="text-accent font-display text-2xl font-bold leading-none" style={{ fontFamily: "var(--font-display)" }}>{p.n}</span>
                    <div>
                      <p className="font-semibold text-sm mb-1">{p.title}</p>
                      <p className="text-primary-foreground/60 text-xs leading-relaxed">{p.text}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            {isPremium ? (
              <div className="bg-card rounded-3xl p-8 border border-foreground/5">
                <p className="text-xs font-bold text-muted-foreground/70 uppercase tracking-[0.2em] mb-4">{t("index.premium.label")}</p>
                <h5 className="font-bold mb-2">{t("index.premium.title")}</h5>
                <p className="text-sm text-muted-foreground mb-6">{t("index.premium.desc")}</p>
                <Link to="/history" className="block text-center w-full py-3 rounded-xl border border-foreground text-sm font-bold hover:bg-foreground hover:text-background transition-all">
                  {t("index.premium.cta")}
                </Link>
              </div>
            ) : (
              <div id="pricing" className="bg-card rounded-3xl p-8 border border-foreground/5">
                <p className="text-xs font-bold text-muted-foreground/70 uppercase tracking-[0.2em] mb-4">{t("index.demo.pricing.label")}</p>
                <h5 className="font-bold mb-2">{t("index.demo.pricing.title")}</h5>
                <p className="text-sm text-muted-foreground mb-6">{t("index.demo.pricing.desc")}</p>
                <Link to="/app" className="block text-center w-full py-3 rounded-xl border border-foreground text-sm font-bold hover:bg-foreground hover:text-background transition-all">
                  {t("index.demo.pricing.cta")}
                </Link>
                <p className="text-[10px] text-muted-foreground/70 mt-3 text-center">{t("index.demo.pricing.free")}</p>
              </div>
            )}
          </div>
        </div>
      </section>

      <section id="methode" className="bg-primary text-primary-foreground py-32 px-6">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-16">
          {[
            { title: t("index.method.b1.title"), text: t("index.method.b1.text"), shape: "rotate" },
            { title: t("index.method.b2.title"), text: t("index.method.b2.text"), shape: "circle" },
            { title: t("index.method.b3.title"), text: t("index.method.b3.text"), shape: "dot" },
          ].map((b) => (
            <div key={b.title} className="space-y-4">
              <div className="size-12 rounded-2xl bg-accent/20 flex items-center justify-center">
                {b.shape === "rotate" && <div className="size-5 border-2 border-accent rounded-sm rotate-45" />}
                {b.shape === "circle" && <div className="size-5 border-2 border-accent rounded-full" />}
                {b.shape === "dot" && <div className="size-5 bg-accent rounded-full" />}
              </div>
              <h3 className="text-xl font-bold">{b.title}</h3>
              <p className="text-primary-foreground/50 leading-relaxed">{b.text}</p>
            </div>
          ))}
        </div>
      </section>

      <SpecializedProtocolsSection />

      <footer className="py-12 border-t border-foreground/5 px-6">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-8">
          <div className="flex items-center gap-2 opacity-60">
            <div className="size-6 bg-foreground rounded-md" />
            <span className="font-display font-bold text-sm tracking-tight uppercase" style={{ fontFamily: "var(--font-display)" }}>
              {t("header.brand")}
            </span>
          </div>
          <p className="text-xs text-muted-foreground">{t("index.footer.copy")}</p>
          <div className="flex gap-6">
            <a href="#privacy" className="text-xs font-bold uppercase tracking-widest text-muted-foreground hover:text-accent">{t("index.footer.privacy")}</a>
            {!isPremium && (
              <a href="#pricing" className="text-xs font-bold uppercase tracking-widest text-muted-foreground hover:text-accent">{t("index.footer.premium")}</a>
            )}
          </div>
        </div>
      </footer>
    </div>
  );
}

// Slogans dispersés en arrière-plan, à gauche/droite, à différentes hauteurs.
// Faible opacité + rotation légère pour un effet quasi-subliminal sans nuire à la lisibilité.
function SubliminalSlogans() {
  const t = useT();
  const items: { key: string; side: "left" | "right"; top: string; rotate: string; maxW: string }[] = [
    { key: "slogan.s1", side: "left",  top: "18%", rotate: "-4deg",  maxW: "16rem" },
    { key: "slogan.s2", side: "right", top: "34%", rotate: "3deg",   maxW: "18rem" },
    { key: "slogan.s3", side: "left",  top: "52%", rotate: "-2deg",  maxW: "20rem" },
    { key: "slogan.s4", side: "right", top: "68%", rotate: "4deg",   maxW: "16rem" },
    { key: "slogan.s5", side: "left",  top: "82%", rotate: "-3deg",  maxW: "18rem" },
    { key: "slogan.s6", side: "right", top: "92%", rotate: "2deg",   maxW: "14rem" },
  ];
  return (
    <div aria-hidden="true" className="hidden lg:block pointer-events-none absolute inset-0 z-0 select-none">
      {items.map((it) => (
        <span
          key={it.key}
          className="absolute font-display italic text-foreground/[0.06] text-sm leading-snug tracking-tight"
          style={{
            top: it.top,
            [it.side]: "1.5rem",
            transform: `rotate(${it.rotate})`,
            maxWidth: it.maxW,
            fontFamily: "var(--font-display)",
          } as CSSProperties}
        >
          {t(it.key)}
        </span>
      ))}
    </div>
  );
}

// Section "Parcours spécialisés" — 4 cartes vers /protocols pour faire
// remonter la niche dans le SEO (chaque carte est un Link interne).
function SpecializedProtocolsSection() {
  const t = useT();
  const { locale } = useLocale();
  const [showAll, setShowAll] = useState(false);
  const [cat, setCat] = useState<string>("all");

  const EMOJI: Record<string, string> = {
    post_aesthetic: "✨", pre_post_op: "🩺", meno_andro: "🌙", onco_recovery: "🎗️",
    acne: "🧴", scars: "🩹", recomp: "💪", tattoo_piercing: "🎨", sleep_eyes: "😴",
    sobriety: "🌱", rosacea_reactive: "🌡️", melasma: "☀️", mole_abcde: "🔬",
    eczema_psoriasis: "🧬", hairloss_density: "💇", hair_graft: "🌾",
    beard_brows: "🧔", posture: "🧍", cellulite: "🦵",
    pregnancy_postpartum: "🤱", hormonal_cycle: "🩸", teeth_smile: "🦷",
  };
  const CAT_OF: Record<string, "skin"|"hair"|"body"|"hormonal"|"wellness"> = {
    acne: "skin", scars: "skin", rosacea_reactive: "skin", melasma: "skin",
    mole_abcde: "skin", eczema_psoriasis: "skin", post_aesthetic: "skin",
    hairloss_density: "hair", hair_graft: "hair", beard_brows: "hair",
    recomp: "body", posture: "body", cellulite: "body", tattoo_piercing: "body",
    meno_andro: "hormonal", hormonal_cycle: "hormonal", pregnancy_postpartum: "hormonal",
    onco_recovery: "hormonal", pre_post_op: "hormonal",
    sleep_eyes: "wellness", sobriety: "wellness", teeth_smile: "wellness",
  };
  const CATS: { key: string; label: Record<string,string> }[] = [
    { key: "all",      label: { fr: "Tous",      en: "All",       nl: "Alle" } },
    { key: "skin",     label: { fr: "Peau",      en: "Skin",      nl: "Huid" } },
    { key: "hair",     label: { fr: "Cheveux",   en: "Hair",      nl: "Haar" } },
    { key: "body",     label: { fr: "Corps",     en: "Body",      nl: "Lichaam" } },
    { key: "hormonal", label: { fr: "Hormonal",  en: "Hormonal",  nl: "Hormonaal" } },
    { key: "wellness", label: { fr: "Bien-être", en: "Wellness",  nl: "Welzijn" } },
  ];
  const filtered = cat === "all" ? PROTOCOLS : PROTOCOLS.filter((p) => CAT_OF[p.goal] === cat);
  const visible = showAll || cat !== "all" ? filtered : filtered.slice(0, 6);
  return (
    <section id="specialized" className="py-14 sm:py-24 px-5 sm:px-6 border-t border-foreground/5">
      <div className="max-w-7xl mx-auto">
        <div className="text-center max-w-2xl mx-auto mb-8 sm:mb-12">
          <span className="inline-block px-4 py-1.5 rounded-full bg-accent/10 text-accent text-xs font-bold tracking-widest uppercase mb-4">
            {t("index.specialized.kicker")}
          </span>
          <h2 className="font-display font-bold text-2xl sm:text-3xl md:text-5xl tracking-tight mb-4 leading-tight" style={{ fontFamily: "var(--font-display)" }}>
            {t("index.specialized.title")}
          </h2>
          <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">{t("index.specialized.subtitle")}</p>
        </div>
        {/* Category chips — horizontal scroll on mobile, no scrollbar */}
        <div className="-mx-5 sm:mx-0 mb-6 sm:mb-8 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          <div className="flex gap-2 px-5 sm:px-0 sm:flex-wrap sm:justify-center w-max sm:w-auto">
            {CATS.map((c) => {
              const active = cat === c.key;
              const count = c.key === "all"
                ? PROTOCOLS.length
                : PROTOCOLS.filter((p) => CAT_OF[p.goal] === c.key).length;
              return (
                <button
                  key={c.key}
                  onClick={() => { setCat(c.key); setShowAll(false); }}
                  className={`shrink-0 text-xs font-bold px-3.5 py-2 rounded-full border transition-colors ${
                    active
                      ? "bg-foreground text-background border-foreground"
                      : "border-foreground/15 text-foreground/70 hover:border-accent hover:text-accent"
                  }`}
                >
                  {c.label[locale] ?? c.label.en} <span className="opacity-60">· {count}</span>
                </button>
              );
            })}
          </div>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          {visible.map((p) => (
            <Link
              key={p.goal}
              to="/protocols/$goal"
              params={{ goal: p.goal }}
              className="group relative block rounded-2xl border border-foreground/10 p-3.5 sm:p-6 hover:border-accent/60 hover:bg-accent/5 active:scale-[0.98] transition-all min-h-[148px] sm:min-h-[180px]"
            >
              {p.tier === "pro" && (
                <span className="absolute top-2.5 right-2.5 text-[9px] font-bold tracking-widest uppercase px-1.5 py-0.5 rounded-md bg-accent/15 text-accent">
                  Pro
                </span>
              )}
              <div className="text-2xl sm:text-3xl mb-2 leading-none" aria-hidden>{EMOJI[p.goal] ?? "•"}</div>
              <h3 className="font-display font-bold text-[15px] sm:text-lg mb-1.5 sm:mb-2 tracking-tight leading-tight" style={{ fontFamily: "var(--font-display)" }}>
                {p.title[locale]}
              </h3>
              <p className="text-[11px] sm:text-xs text-muted-foreground leading-snug sm:leading-relaxed mb-3 line-clamp-2 sm:line-clamp-none">{p.subtitle[locale]}</p>
              <span className="text-xs font-bold text-foreground group-hover:text-accent transition-colors hidden sm:inline">
                {t("index.specialized.card.cta")} →
              </span>
            </Link>
          ))}
        </div>
        {visible.length === 0 && (
          <p className="text-center text-sm text-muted-foreground mt-6">—</p>
        )}
        <div className="text-center mt-8 sm:mt-10 flex flex-col sm:flex-row gap-3 justify-center items-center">
          {cat === "all" && !showAll && PROTOCOLS.length > 6 && (
            <button
              onClick={() => setShowAll(true)}
              className="text-sm font-bold px-5 py-2.5 rounded-full border border-foreground/20 hover:border-accent hover:text-accent transition-colors"
            >
              {t("index.specialized.card.cta")} ({PROTOCOLS.length - 6}+) →
            </button>
          )}
          <Link to="/protocols" className="text-sm font-bold underline decoration-accent underline-offset-4 hover:text-accent">
            {t("index.specialized.all")}
          </Link>
        </div>
      </div>
    </section>
  );
}
